/**
 * EvidenceGradeBadge — how much to trust the DETECTION MECHANISM behind a finding, Suggestion or
 * Task (`DETECTORS.md` §2.4: "How much to trust the mechanism, shown on every finding"). This is
 * a state a check can only move UPWARD through via validated measurement — never because a user
 * liked a finding — so it composes `StatusBadge` (state), not `Tag` (category qualifier). Distinct
 * from `EvidenceChip`, which grades one VALUE's classification (observed/estimated/inferred/
 * hypothesis); this grades the check that produced it. Every finding, Suggestion card and Task
 * shows this — the grade is explicitly "not internal metadata" (`DETECTORS.md` §2.4).
 *
 * Copy is verbatim from `DETECTORS.md` §2.4 / `design-spec.md:313` — "Unproven" is load-bearing:
 * it is the word that satisfies §2.4's "must be labelled as unproven wherever shown" for
 * `hypothesis`. Do not paraphrase it at a call site.
 *
 * DES-38: each grade carries a distinct icon as well as distinct text, so the three states read
 * apart from each other even with colour removed.
 *
 * WHEN: on every finding, Suggestion card and Task drafted from one.
 * NEVER: as a category label (use `Tag` / `EvidenceChip`) or a delivery/lifecycle state (use
 *   `DeliveryVerdictPill` / `StatusStepper`) — a grade is an evaluative trust state, not a
 *   category, and it is not what "delivered" or "done" means.
 */
import { CheckCircle2, BookOpenCheck, CircleHelp } from 'lucide-react'
import { StatusBadge, type StatusBadgeTone } from '@/components/shared/status-badge'
import type { EvidenceGrade } from '@/lib/growth/evidence/types'
// T2 (extract-shared): moved to a server-safe home so a server route/component can render the same
// grade label without pulling this file's client boundary along (ARC-18 — one copy). Copy is still
// verbatim from DETECTORS.md §2.4 / design-spec.md:313 — see `labels.ts` for that provenance note.
import { GRADE_LABEL } from '@/lib/growth/suggestions/labels'
import { cn } from '@/lib/utils'

const GRADE_TONE: Record<EvidenceGrade, StatusBadgeTone> = {
  measured: 'success',
  established_practice: 'neutral',
  hypothesis: 'warning',
}

// A distinct glyph per grade — DES-38 holds on text AND icon, colour is never the only signal.
const GRADE_ICON: Record<EvidenceGrade, typeof CheckCircle2> = {
  measured: CheckCircle2,
  established_practice: BookOpenCheck,
  hypothesis: CircleHelp,
}

export interface EvidenceGradeBadgeProps {
  grade: EvidenceGrade
  className?: string
}

export function EvidenceGradeBadge({
  grade,
  className,
}: EvidenceGradeBadgeProps): React.ReactElement {
  const Icon = GRADE_ICON[grade]
  return (
    <StatusBadge tone={GRADE_TONE[grade]} className={cn('gap-1', className)}>
      <Icon className="size-3" aria-hidden="true" />
      {GRADE_LABEL[grade]}
    </StatusBadge>
  )
}
